import React, { useState, useEffect } from 'react';
import { getBaseUrl } from '../../../shared/utils/apiConfig';
import { usePlatformAdminAuth } from '../contexts/PlatformAdminAuthContext';

/**
 * Alert Thresholds Page
 * 
 * MIGRATED from tenant dashboard (P-ADMIN-1.4)
 * Original: /src/features/admin/components/system-config/AlertThresholds.tsx
 * 
 * ML alert threshold configuration affecting ALL tenants:
 * - Severity thresholds (critical, high, medium, low)
 * - Alert TTL (time-to-live)
 * - Analyst feedback influence weight
 * 
 * Security: Requires platform admin authentication
 */

type AlertSeverity = 'critical' | 'high' | 'medium' | 'low';

interface ThresholdSettings {
  critical: number;
  high: number;
  medium: number;
  low: number;
}

interface AlertSettings {
  thresholds: ThresholdSettings;
  ttlMinutes: number;
  analystInfluenceWeight: number;
}

const severityColors: Record<AlertSeverity, { bg: string; text: string; border: string }> = {
  critical: { bg: 'bg-red-900/50', text: 'text-red-200', border: 'border-red-700' },
  high: { bg: 'bg-orange-900/50', text: 'text-orange-200', border: 'border-orange-700' },
  medium: { bg: 'bg-yellow-900/50', text: 'text-yellow-200', border: 'border-yellow-700' },
  low: { bg: 'bg-blue-900/50', text: 'text-blue-200', border: 'border-blue-700' }
};

export const AlertThresholds: React.FC = () => {
  const [settings, setSettings] = useState<AlertSettings>({
    thresholds: {
      critical: 90,
      high: 75,
      medium: 50,
      low: 25
    },
    ttlMinutes: 120,
    analystInfluenceWeight: 60
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { platformAdmin } = usePlatformAdminAuth();

  useEffect(() => {
    const fetchAlertThresholds = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('astrapeak_platform_admin_token');
        
        const response = await fetch(`${getBaseUrl()}/api/platform-admin/alert-thresholds`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch thresholds: ${response.statusText}`);
        }
        
        const data = await response.json();
        setSettings(data.settings);
        setError(null);
      } catch (err) {
        console.error('Error fetching alert thresholds:', err);
        setError('Failed to load alert thresholds');
        // Fallback to initial settings on error
      } finally {
        setLoading(false);
      }
    };

    fetchAlertThresholds();
  }, []);

  const handleThresholdChange = (level: AlertSeverity, value: number) => {
    setSettings(prev => ({
      ...prev,
      thresholds: {
        ...prev.thresholds,
        [level]: value
      }
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('astrapeak_platform_admin_token');
      
      const response = await fetch(`${getBaseUrl()}/api/platform-admin/alert-thresholds`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ settings })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to save thresholds: ${response.statusText}`);
      }
      
      setHasChanges(false);
      setError(null);
    } catch (error) {
      console.error('Error saving alert thresholds:', error);
      setError('Failed to save settings. Please try again.');
    } finally {
      setLoading(false);
      setShowConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-white">Alert Thresholds</h2>
          <p className="text-gray-400 mt-1">Configure ML alert severity thresholds and feedback settings</p>
        </div>
        {error && <p className="text-red-400 text-sm">{error}</p>}
      </div>

      {/* Warning Banner */}
      <div className="mb-6 p-4 bg-yellow-900/30 border border-yellow-700/50 rounded-lg">
        <div className="flex items-start space-x-3">
          <svg className="w-5 h-5 text-yellow-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div>
            <p className="text-sm font-medium text-yellow-200">ML Training Impact</p>
            <p className="text-sm text-yellow-300/80 mt-1">Changes to these thresholds will affect ML model training and alert classification for ALL tenants.</p>
          </div>
        </div>
      </div>

      {/* Severity Thresholds */}
      <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6 mb-6">
        <h3 className="text-white font-semibold text-lg mb-4">Severity-Based Thresholds</h3>
        <p className="text-sm text-gray-400 mb-6">ML confidence scores above these thresholds will be classified at the corresponding severity level.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {(['critical', 'high', 'medium', 'low'] as const).map(level => (
            <div key={level} className={`space-y-3 p-4 rounded-lg border ${severityColors[level].bg} ${severityColors[level].border}`}>
              <div className="flex justify-between items-center">
                <label className="text-sm font-medium text-white capitalize">{level} Threshold</label>
                <span className={`px-3 py-1 text-sm font-semibold rounded-full ${severityColors[level].bg} ${severityColors[level].text} border ${severityColors[level].border}`}>
                  {settings.thresholds[level]}%
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                step="5"
                value={settings.thresholds[level]}
                onChange={(e) => handleThresholdChange(level, parseInt(e.target.value))}
                className="w-full accent-purple-500"
              />
              <div className="flex justify-between text-xs text-gray-400">
                <span>0%</span>
                <span>50%</span>
                <span>100%</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* TTL Settings */}
      <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6 mb-6">
        <h3 className="text-white font-semibold text-lg mb-4">Alert Expiry Policy</h3>
        <p className="text-sm text-gray-400 mb-6">Configure how long alerts remain active before automatic expiration.</p>
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <label className="text-sm font-medium text-gray-300">Time-to-live (TTL)</label>
            <span className="text-sm font-semibold text-purple-400">{settings.ttlMinutes} minutes</span>
          </div>
          <input
            type="range"
            min="15"
            max="1440"
            step="15"
            value={settings.ttlMinutes}
            onChange={(e) => {
              setSettings(prev => ({ ...prev, ttlMinutes: parseInt(e.target.value) }));
              setHasChanges(true);
            }}
            className="w-full accent-purple-500"
          />
          <div className="flex justify-between text-xs text-gray-400">
            <span>15 min</span>
            <span>12 hours</span>
            <span>24 hours</span>
          </div>
        </div>
      </div>

      {/* Analyst Feedback */}
      <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
        <h3 className="text-white font-semibold text-lg mb-4">Analyst Feedback Influence</h3>
        <div className="mb-6 p-4 bg-purple-900/30 border border-purple-700/50 rounded-lg">
          <p className="text-sm text-purple-200">
            This determines how much analyst-labeled feedback (True Positive/False Positive/False Negative) affects ML model training and alert scoring. Higher values apply faster learning from analyst corrections.
          </p>
        </div>
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <label className="text-sm font-medium text-gray-300">Feedback Weight</label>
            <span className="text-sm font-semibold text-purple-400">{settings.analystInfluenceWeight}%</span>
          </div>
          <input
            type="range"
            min="0"
            max="100"
            step="5"
            value={settings.analystInfluenceWeight}
            onChange={(e) => {
              setSettings(prev => ({ ...prev, analystInfluenceWeight: parseInt(e.target.value) }));
              setHasChanges(true);
            }}
            className="w-full accent-purple-500"
          />
          <div className="flex justify-between text-xs text-gray-400">
            <span>No Influence</span>
            <span>Balanced</span>
            <span>High Influence</span>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="mt-6 pt-6 border-t border-gray-700 flex justify-end">
        <button
          className={`px-6 py-2.5 rounded-lg text-white font-medium transition-colors ${
            hasChanges && !loading
              ? 'bg-purple-600 hover:bg-purple-700'
              : 'bg-gray-600 cursor-not-allowed'
          }`}
          disabled={!hasChanges || loading}
          onClick={() => setShowConfirm(true)}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Confirm Dialog */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 max-w-md w-full mx-4">
            <h4 className="text-lg font-semibold text-white mb-4">Confirm Changes</h4>
            <p className="text-gray-300 mb-6">
              Are you sure you want to update the alert thresholds? <strong className="text-yellow-400">This will affect how alerts are classified and processed for all tenants</strong>, and will impact ML model training.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                onClick={() => setShowConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors"
                onClick={handleSave}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlertThresholds;
